import {getLayoutData, getPageData, getPages} from "@/pages/api/api";
import ExtendedContent from "@/components/common/ExtendedContent";
import useSetBannerData from "@/hooks/useSetBannerData";
import {
	SEE_ALSO_INFO_BLOCK_QUERY,
	SINGLE_PAGE_QUERY,
	WHAT_WE_DO_CATEGORY_QUERY,
	WHAT_WE_DO_SINGLE_ACTION_QUERY
} from "@/pages/api/fragments";
import {CONTENT_TYPES, PAGES, SINGLE_PAGE_TYPES} from "@/constants";
import SeeAlso, {getSeeAlsoData, getSeeAlsoInfo} from "@/components/SeeAlso";
import {useMemo} from "react";
import {getPathsQueryString} from "@/utils/utils";

export default function Page({data, locale, menu}) {
	const content = data?.content || []
	const bannerData = useSetBannerData(data)
	const seeAlsoInfo = getSeeAlsoInfo(data)
	const formattedSeeAlsoData = useMemo(() => getSeeAlsoData(data), [data])

	return (
		<div>
			<ExtendedContent content={content} globalBannerData={bannerData}/>
			<SeeAlso
				header={seeAlsoInfo.header}
				variant={seeAlsoInfo.variant}
				data={formattedSeeAlsoData}
			/>
		</div>
	)
}

const EXCLUDE_PATHS = [
	PAGES.LOOKING_FOR_HELP
]

const pathsQuery = `
	query GetSearchForHelpPages($locale: SiteLocale) {
		allSinglePages(locale: $locale, filter: {slug: {notIn: ${getPathsQueryString(EXCLUDE_PATHS)} }, pageType: {eq: "${SINGLE_PAGE_TYPES.LOOKING_FOR_HELP}"}}) {
			singlePageName
			pageType
            slug
		}
	}
`;

const propsQuery = `
	query GetSearchForHelpPagesData($slug: String, $locale: SiteLocale)  {
		allSinglePages(locale: $locale, filter: {slug: {eq: $slug}}) {
			__typename
            ${SINGLE_PAGE_QUERY}
			seeAlsoInfo{
				${SEE_ALSO_INFO_BLOCK_QUERY}
			}
			seeAlsoLinks {
				__typename
			  ... on ${CONTENT_TYPES.WHAT_WE_DO_SINGLE_ACTION} {
				${WHAT_WE_DO_SINGLE_ACTION_QUERY}
			  }
			  ... on ${CONTENT_TYPES.WHAT_WE_DO_CATEGORY} {
				${WHAT_WE_DO_CATEGORY_QUERY}
			  }
			  ... on ${CONTENT_TYPES.SINGLE_PAGE} {
				  __typename
				  id
				  pageType
				  slug
				  card{
					header
					description
				  }
			  }
		    }
        }
    }
`

export async function getStaticProps({params, locale}) {
	const {slug} = params;
	const data = await getPageData('allSinglePages', slug, propsQuery, locale);
	const layout = await getLayoutData(locale)

	return {
		props: {
			data,
			locale,
			...layout,
			isDarkSearch: true
		},
		revalidate: 60 * 2
	};
}

export async function getStaticPaths({locales}) {
	const paths = [];
	for (const locale of locales) {
		const pages = await getPages('allSinglePages', pathsQuery, locale);
		const newsPage = pages.map((page) => {
			return {
				params: {
					slug: page.slug,
				},
				locale,
			};
		})
			.filter((el) => {
				return el !== null;
			});
		paths.push(...newsPage);
	}
	return {
		paths,
		fallback: false,
	};
}
